import { PageMeta } from "@/components/PageMeta";
import { SectionHeading } from "@/components/SectionHeading";
import { GlassPanel } from "@/components/GlassPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { CtaLink } from "@/components/Button";
import { Keycap } from "@/components/keycap/Keycap";
import { signInCta, signUpCta } from "@/config/site";
import { routeSeo } from "@/content/seo";

const pillars = [
  {
    title: "Organisations and workspaces",
    status: "live" as const,
    body: "Every identity belongs to an organisation. Personal and team-scoped workspaces group the projects, components and configuration an organisation is actively working on.",
  },
  {
    title: "Roles and permissions",
    status: "live" as const,
    body: "Access is enforced through role-aware, deny-by-default authorization rather than inline, ad hoc permission checks -- including a real organisation-suspension check no member, including an owner, can bypass.",
  },
  {
    title: "Billing and plans",
    status: "live" as const,
    body: "Real Stripe subscriptions, invoices and plan changes, self-serve from inside Devory Cloud. See Pricing for what each plan includes.",
  },
  {
    title: "Cloud workflow execution",
    status: "live" as const,
    body: "A safety-scoped set of workflow components (input, output, constant, logger) run entirely in Devory Cloud today, no local Desktop required. Broader execution coverage is active work, not yet available.",
  },
  {
    title: "Shared libraries",
    status: "in-development" as const,
    body: "Qualified components can move from a private library into a library shared across a team's workspace.",
  },
  {
    title: "Deployment and runtime state",
    status: "in-development" as const,
    body: "Devory Cloud tracks deployment and runtime state as part of its services. Binding that state to live, customer-controlled environments is in progress, not generally available.",
  },
];

export function CloudPage() {
  const seo = routeSeo["/cloud"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/cloud" />

      <section className="container page-hero">
        <p className="eyebrow">Devory Cloud</p>
        <h1 className="page-hero-title">The shared canvas for identity and collaboration</h1>
        <p className="page-hero-lede">
          Devory Cloud is where organisations, workspaces, shared libraries and deployment state
          live. It is the counterpart to the local canvas, not a separate product.
        </p>
        <p className="page-hero-lede">
          Public Release 0.1 is live: create a free account and start working today, at the
          canonical Devory Cloud hostname, not a stand-in.
        </p>
        <div className="cta-banner-actions">
          <CtaLink to={signUpCta.path} variant="primary">
            {signUpCta.label}
          </CtaLink>
          <CtaLink to={signInCta.path} variant="secondary">
            {signInCta.label}
          </CtaLink>
        </div>
      </section>

      <section className="container section-tight page-diagram">
        <Keycap id="cloud" size="xl" state="active" />
      </section>

      <section className="container section" aria-labelledby="cloud-pillars-heading">
        <SectionHeading
          level={2}
          title={<span id="cloud-pillars-heading">What Devory Cloud provides</span>}
        />
        <div className="grid-2">
          {pillars.map((pillar) => (
            <GlassPanel as="article" key={pillar.title} className="audience-card">
              <div className="capability-card-head">
                <h3>{pillar.title}</h3>
                <StatusBadge status={pillar.status} />
              </div>
              <p>{pillar.body}</p>
            </GlassPanel>
          ))}
        </div>
      </section>

      <section className="container section-tight" aria-labelledby="cloud-limits-heading">
        <GlassPanel className="problem-card">
          <SectionHeading
            level={2}
            title={<span id="cloud-limits-heading">What Devory Cloud does not claim today</span>}
          />
          <ul className="list-check">
            <li>
              Cloud workflow execution is scoped to a small, safety-reviewed set of components
              today, not arbitrary or custom code
            </li>
            <li>Shared libraries and customer-bound deployment environments are still in development</li>
            <li>No specific SLA, uptime commitment or data-residency guarantee is published</li>
            <li>No SOC 2, ISO 27001, PCI DSS or other compliance certification is claimed</li>
          </ul>
        </GlassPanel>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Evaluating Devory Cloud for a team?</h2>
          <p>
            See how governance, roles and auditability extend to organisations, or get in touch.
          </p>
          <div className="cta-banner-actions">
            <CtaLink to="/enterprise" variant="secondary">
              Read about enterprise governance
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
